/**
 * search-registry.ts -- the single source of truth for what the global
 * search (cmd+K) can find beyond user data.
 *
 * ============================ PERMANENT RULE ============================
 * (PO, 14-Jul-2026 -- part of the DEFINITION OF DONE for any UI delivery,
 * alongside the iOS 26 + responsive rule)
 *
 *   ANY new or renamed page, route, tab or placeholder MUST be indexed
 *   here before the delivery is considered done:
 *     - PAGES entry: { path, en, ro, keywords[] } -- keywords lowercase,
 *       BOTH languages, common synonyms, plus diacritics-free Romanian
 *       (how people actually type: "setari", "securitate", "blocare").
 *     - Help guides follow the extended publishing model:
 *       HTML in public/guides + GUIDES entry here + 2 i18n keys.
 *
 *   A route that is not in this file is invisible to search -- which,
 *   for the person who does not know where a feature lives, means the
 *   feature might as well not exist.
 * ========================================================================
 *
 * Consumed by components/GlobalSearch.tsx (pages rendered as the FIRST
 * section -- a short query most likely means navigation). Deep targets
 * use query params the pages understand: ?tab= (Settings), ?open=<id>
 * (Contacts drawer), ?recipient= (Messages pre-filtered history).
 */

export type PageEntry = {
  path: string;
  en: string;
  ro: string;
  keywords: string[];
};

export type GuideEntry = { titleKey: string; href: string; slug?: string };

/** Resolve the {lang} token in localized guide hrefs (ro | en fallback).
 * Non-localized hrefs pass through unchanged. Used by HelpPage and
 * GlobalSearch so both surfaces open the same language variant. */
export function resolveGuideHref(href: string, lang: string): string {
  return href.replace("{lang}", lang === "ro" ? "ro" : "en");
}

export const PAGES: PageEntry[] = [
  { path: "/dashboard", en: "Dashboard", ro: "Dashboard", keywords: ["dashboard", "home", "acasa", "panou"] },
  { path: "/templates", en: "Templates", ro: "\u0218abloane", keywords: ["templates", "sabloane", "template", "model", "mesaje salvate"] },
  { path: "/attachments", en: "Attachments", ro: "Ata\u0219amente", keywords: ["attachments", "atasamente", "fisiere", "files", "media", "mms"] },
  { path: "/senders", en: "Channels \u0026 Senders", ro: "Canale \u0219i expeditori", keywords: ["senders", "expeditori", "sender id", "canale", "channels", "sender"] },
  { path: "/whatsapp", en: "WhatsApp", ro: "WhatsApp", keywords: ["whatsapp", "wa", "template wa", "business"] },
  { path: "/wallet", en: "Wallet \u0026 top-up", ro: "Portofel \u0219i reincarcare", keywords: ["wallet", "portofel", "top-up", "reincarcare", "credit", "sold", "balance", "plata"] },
  { path: "/messages", en: "Message History", ro: "Istoric mesaje", keywords: ["messages", "history", "mesaje", "istoric", "sms"] },
  { path: "/inbox", en: "Inbox", ro: "Inbox", keywords: ["inbox", "primite", "received", "mo", "auto-reply", "raspuns"] },
  { path: "/campaigns", en: "Campaigns", ro: "Campanii", keywords: ["campaigns", "campanii", "bulk", "broadcast"] },
  { path: "/contacts", en: "Contacts", ro: "Contacte", keywords: ["contacts", "contacte", "agenda", "grupuri", "groups"] },
  { path: "/blocklist", en: "Blocklist", ro: "Blocklist", keywords: ["blocklist", "block", "blocare", "blocati", "stop", "optout", "opt-out"] },
  { path: "/short-url", en: "Short URL", ro: "URL scurtat", keywords: ["short url", "url scurt", "link scurt", "scurtare", "shortener", "short link", "1st.is", "1st", "linkuri"] },
  { path: "/qr", en: "QR Code", ro: "Cod QR", keywords: ["qr", "qr code", "cod qr", "scan", "scanare", "generator qr"] },
  { path: "/otp", en: "OTP / 2FA", ro: "OTP / 2FA", keywords: ["otp", "2fa", "cod", "code", "verificare", "verification", "autentificare", "one-time", "parola unica"] },
  { path: "/mnp", en: "Number portability", ro: "Portabilitate numere", keywords: ["mnp", "portability", "portabilitate", "portat", "ported", "operator", "retea", "network", "lookup", "cautare numar"] },
  { path: "/api-keys", en: "API Keys", ro: "Chei API", keywords: ["api", "keys", "chei", "cheie", "token", "secret", "integrare", "integration", "developer", "v1"] },
  { path: "/webhooks", en: "Webhooks", ro: "Webhooks", keywords: ["webhook", "webhooks", "callback", "notificari", "notifications", "message.status", "hmac", "semnatura", "signature", "endpoint"] },
  { path: "/sms2mail", en: "SMS \u2192 E-mail", ro: "SMS \u2192 E-mail", keywords: ["sms2mail", "sms to email", "sms to e-mail", "sms email", "forward", "forwarding", "redirectionare", "redirec\u021bionare", "e-mail", "email", "inbound", "mo", "mesaje primite", "receptie", "adresa verificata", "verified address"] },
  { path: "/mail2sms", en: "E-mail \u2192 SMS", ro: "E-mail \u2192 SMS", keywords: ["mail2sms", "email to sms", "e-mail to sms", "email sms", "mail sms", "alias", "outlook", "gmail", "erp", "expeditor", "expeditori", "sender", "senders", "trimite sms din email", "trimite sms din e-mail", "1st.is"] },
  { path: "/api-playground", en: "API Playground", ro: "API Playground", keywords: ["playground", "api playground", "test api", "testeaza api", "curl", "request", "executa request", "developer", "sandbox"] },
  { path: "/reports", en: "Reports", ro: "Rapoarte", keywords: ["reports", "rapoarte", "export", "csv", "statistici", "stats"] },
  { path: "/invoices", en: "Invoices", ro: "Facturi", keywords: ["invoices", "facturi", "billing", "facturare", "plati"] },
  { path: "/prices", en: "Your prices", ro: "Pre\u021burile tale", keywords: ["prices", "preturi", "pret", "price", "tarif", "tarife", "cost", "costuri", "sender id", "short code", "pricing"] },
  { path: "/help", en: "Help", ro: "Ajutor", keywords: ["help", "ajutor", "ghiduri", "guides", "docs", "documentatie"] },
  { path: "/settings", en: "Settings", ro: "Set\u0103ri", keywords: ["settings", "setari", "cont", "account", "profil", "profile"] },
  { path: "/settings?tab=security", en: "Settings \u00b7 Security", ro: "Set\u0103ri \u00b7 Securitate", keywords: ["security", "securitate", "mfa", "2fa", "totp", "parola", "password", "sesiuni", "sessions"] },
  { path: "/settings?tab=limits", en: "Settings \u00b7 Limits", ro: "Set\u0103ri \u00b7 Limite", keywords: ["limits", "limite", "viteza", "throttle", "msg/s", "rate"] },
  { path: "/settings?tab=billing", en: "Settings \u00b7 Billing", ro: "Set\u0103ri \u00b7 Facturare", keywords: ["billing", "facturare", "plata", "payment", "wallet", "sold", "balance"] },
  { path: "/settings?tab=webhook", en: "Settings \u00b7 Inbound messaging", ro: "Set\u0103ri \u00b7 Recep\u021bie mesaje", keywords: ["webhook", "mo webhook", "callback", "inbound", "receptie", "receptie mesaje", "mesaje primite"] },
  { path: "/settings?tab=notifications", en: "Settings \u00b7 Notifications", ro: "Set\u0103ri \u00b7 Notific\u0103ri", keywords: ["notifications", "notificari", "alerte", "alerts"] },
  { path: "/settings?tab=connection", en: "Settings \u00b7 Connection", ro: "Set\u0103ri \u00b7 Conexiune", keywords: ["connection", "conexiune", "api key", "cheie api", "credentiale"] },
];

/** Help guides -- same i18n keys as HelpPage GUIDES; grows with the KB. */
export const GUIDES: GuideEntry[] = [
  {
    titleKey: "helpPage.guide_templates_title",
    href: "/guides/templates.{lang}.html",
    slug: "templates",
  },
  {
    titleKey: "helpPage.guide_send_title",
    href: "/guides/send.{lang}.html",
    slug: "send",
  },
  {
    titleKey: "helpPage.guide_messages_title",
    href: "/guides/messages.{lang}.html",
    slug: "messages",
  },
  {
    titleKey: "helpPage.guide_campaigns_title",
    href: "/guides/campaigns.{lang}.html",
    slug: "campaigns",
  },
  {
    titleKey: "helpPage.guide_contacts_title",
    href: "/guides/contacts.{lang}.html",
    slug: "contacts",
  },
  {
    titleKey: "helpPage.guide_inbox_title",
    href: "/guides/inbox.{lang}.html",
    slug: "inbox",
  },
  {
    titleKey: "helpPage.guide_senders_title",
    href: "/guides/senders.{lang}.html",
    slug: "senders",
  },
  {
    titleKey: "helpPage.guide_blocklist_title",
    href: "/guides/blocklist.{lang}.html",
    slug: "blocklist",
  },
  {
    titleKey: "helpPage.guide_mnp_title",
    href: "/guides/mnp.{lang}.html",
    slug: "mnp",
  },
  {
    titleKey: "helpPage.guide_otp_title",
    href: "/guides/otp.{lang}.html",
    slug: "otp",
  },
  {
    titleKey: "helpPage.guide_qr_title",
    href: "/guides/qr.{lang}.html",
    slug: "qr",
  },
  {
    titleKey: "helpPage.guide_attachments_title",
    href: "/guides/attachments.{lang}.html",
    slug: "attachments",
  },
  {
    titleKey: "helpPage.guide_shorturl_title",
    href: "/guides/short-url.{lang}.html",
    slug: "short-url",
  },
  {
    titleKey: "helpPage.guide_reports_title",
    href: "/guides/reports.{lang}.html",
    slug: "reports",
  },
  {
    titleKey: "helpPage.guide_wallet_title",
    href: "/guides/wallet.{lang}.html",
    slug: "wallet",
  },
  {
    titleKey: "helpPage.guide_invoices_title",
    href: "/guides/invoices.{lang}.html",
    slug: "invoices",
  },
  {
    titleKey: "helpPage.guide_prices_title",
    href: "/guides/prices.{lang}.html",
    slug: "prices",
  },  {
    titleKey: "helpPage.guide_team_title",
    href: "/guides/team.{lang}.html",
    slug: "team",
  },
  {
    titleKey: "helpPage.guide_dashboard_title",
    href: "/guides/dashboard.{lang}.html",
    slug: "dashboard",
  },
  {
    titleKey: "helpPage.guide_settings_title",
    href: "/guides/settings.{lang}.html",
    slug: "settings",
  },
  {
    titleKey: "helpPage.guide_wa_title",
    href: "/guides/whatsapp.{lang}.html",
    slug: "whatsapp",
  },
  {
    titleKey: "helpPage.guide_api_start_title",
    href: "/guides/api-getting-started.{lang}.html",
    slug: "api-getting-started",
  },
  {
    titleKey: "helpPage.guide_api_keys_title",
    href: "/guides/api-keys.{lang}.html",
    slug: "api-keys",
  },
  {
    titleKey: "helpPage.guide_api_messages_title",
    href: "/guides/api-messages.{lang}.html",
    slug: "api-messages",
  },
  {
    titleKey: "helpPage.guide_api_otp_title",
    href: "/guides/api-otp.{lang}.html",
    slug: "api-otp",
  },
  {
    titleKey: "helpPage.guide_api_mnp_title",
    href: "/guides/api-mnp-stats.{lang}.html",
    slug: "api-mnp-stats",
  },
  {
    titleKey: "helpPage.guide_api_errors_title",
    href: "/guides/api-errors.{lang}.html",
    slug: "api-errors",
  },
  {
    titleKey: "helpPage.guide_api_webhooks_title",
    href: "/guides/api-webhooks.{lang}.html",
    slug: "api-webhooks",
  },
  {
    titleKey: "helpPage.guide_sms2mail_title",
    href: "/guides/sms2mail.{lang}.html",
    slug: "sms2mail",
  },
  {
    titleKey: "helpPage.guide_api_sms2mail_title",
    href: "/guides/api-sms2mail.{lang}.html",
    slug: "api-sms2mail",
  },
  {
    titleKey: "helpPage.guide_mail2sms_title",
    href: "/guides/mail2sms.{lang}.html",
    slug: "mail2sms",
  },
  {
    titleKey: "helpPage.guide_api_short_links_title",
    href: "/guides/api-short-links.{lang}.html",
    slug: "api-short-links",
  },
  {
    titleKey: "helpPage.guide_data_deletion_title",
    href: "/guides/data-deletion.{lang}.html",
    slug: "data-deletion",
  },
  {
    titleKey: "helpPage.guide_whatsapp_connect_title",
    href: "/guides/whatsapp-connect.{lang}.html",
    slug: "whatsapp-connect",
  },
  {
    titleKey: "helpPage.guide_whatsapp_templates_title",
    href: "/guides/whatsapp-templates.{lang}.html",
    slug: "whatsapp-templates",
  },
  {
    titleKey: "helpPage.guide_api_whatsapp_title",
    href: "/guides/api-whatsapp.{lang}.html",
    slug: "api-whatsapp",
  },
  {
    titleKey: "helpPage.guide_api_playground_title",
    href: "/guides/api-playground.{lang}.html",
    slug: "api-playground",
  },
];
